import React, { useCallback, useEffect, useState } from 'react';
import { TwButton, TwButtonType } from '../../../components/TwButtons/TwButtons';
import TwDialog from '../../../components/TwDialog/TwDialog';
import TwField, { TwInputTypes } from '../../../components/TwFields/TwFields';
import './EditEquipment.scss';
import TwSelect, { TwSelectOption } from '../../../components/TwSelects/TwSelects';

import TwWarning from '../../../components/TwWarning/TwWarning';

import {
  EldConnectionInfo,
  Equipment,
  EquipmentType,
  UpdateEquipmentParams
} from '../../../redux/api/equipment/equipment.model';

import {
  useGetEquipmentByIdQuery,
  useUpdateEquipmentMutation
} from '../../../redux/api/equipment/equipmentApi';
import { useGetAssetIdsQuery, useGetConnectionsQuery } from '../../../redux/api/eld/eldApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { SelectChangeEvent } from '@mui/material/Select';
import useFeatureFlags from '../../../providers/FeatureFlags/FeatureFlags.provider';
import useNotifications from '../../../providers/Notifications/Notifications.provider';

export interface EditEquipmentProps {
  open: boolean;
  equipmentId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const EditEquipment: React.FC<EditEquipmentProps> = ({
  open,
  equipmentId,
  onClose,
  onSuccess
}): JSX.Element => {
  const { enableLiveTracking } = useFeatureFlags();
  const { data: equipment } = useGetEquipmentByIdQuery(equipmentId, {
    skip: !equipmentId || !open
  });
  const [updateEquipment] = useUpdateEquipmentMutation();
  const [equipmentName, setEquipmentName] = useState<string>('');
  const [equipmentType, setEquipmentType] = useState<EquipmentType>(EquipmentType.truck);
  const [eldConnection, setEldConnection] = useState<EldConnectionInfo | undefined>(undefined);
  const [eldAssetId, setEldAssetId] = useState<string | undefined>('');
  const [connectionsOptions, setConnectionsOptions] = useState<TwSelectOption[]>([]);
  const [assetIdsOptions, setAssetIdsOptions] = useState<TwSelectOption[]>([]);

  const [showWarning, setShowWarning] = useState<boolean>(false);

  const { addNotification } = useNotifications();
  const { data: connections = { content: [], total: 0 } } = useGetConnectionsQuery({});

  useEffect(() => {
    if (connections.content && connections.content.length > 0) {
      const options = connections.content.map((connection) => ({
        label: connection.name,
        value: connection.id
      }));
      setConnectionsOptions(options);
    }
  }, [connections]);

  /** Submits the new information for the equipment */
  const handleSubmit = (): void => {
    if (!equipmentName.trim()) {
      addNotification({
        message: 'You must provide an equipment name or number.',
        variant: 'error',
        position: 'bottomRight',
        timeToShow: 3000
      });
      return;
    }
    const assetId = eldAssetId
      ? assetIds.content.find((asset) => asset.value === eldAssetId)
      : undefined;

    const params: UpdateEquipmentParams = {
      id: equipmentId,
      name: equipmentName.trim(),
      type: equipmentType,
      eld_connection_id: eldConnection?.id,
      asset_eld_id: eldAssetId,
      asset_eld_id_key: assetId?.key
    };
    updateEquipment(params)
      .unwrap()
      .then(() => {
        onSuccess && onSuccess();
        addNotification({
          message: 'Equipment updated successfully',
          variant: 'success',
          position: 'bottomRight',
          timeToShow: 3000
        });
        onClose();
      });
  };

  const handleClose = (): void => {
    onClose();
  };

  useEffect(() => {
    if (!equipment) return;
    setEquipmentName(equipment.name);
    setEquipmentType(equipment.type);
    setEldConnection(equipment.eld_connection);
    setEldAssetId(equipment.asset_eld_id);
  }, [equipment]);

  const handleConnectionChange = (e: SelectChangeEvent<string>): void => {
    const connection = connections.content.find((connection) => connection.id === e.target.value);
    setEldConnection(connection);
    setEldAssetId(undefined);
  };

  const { data: assetIds = { content: [], total: 0 } } = useGetAssetIdsQuery(
    eldConnection && eldConnection.id
      ? {
          connectionId: eldConnection.id
        }
      : skipToken
  );

  useEffect(() => {
    if (assetIds.content && assetIds.content.length > 0) {
      const options = assetIds.content.map((assetId) => ({
        label: assetId.value,
        value: assetId.value
      }));
      setAssetIdsOptions(options);
    }
  }, [assetIds]);

  const handleAssetChange = (e: SelectChangeEvent<string>): void => {
    setEldAssetId(e.target.value);
  };

  const entityType = 'equipment';
  return (
    <>
      <TwDialog
        open={open && !showWarning}
        onClickClose={handleClose}
        size="sm"
        title="Edit equipment"
        actions={[
          <TwButton
            key="1"
            fullwidth={false}
            type={TwButtonType.secondary}
            label="Cancel"
            action={handleClose}
          />,
          <TwButton
            key="2"
            fullwidth={false}
            type={TwButtonType.primary}
            label="Save"
            action={handleSubmit}
          />
        ]}
        draggable
        sx={{ '& .MuiDialogContent-root': { padding: '35px 20px !important' } }}
      >
        <div className="edit-equipment__wrapper tid-edit-equipment__wrapper">
          <div className="edit-equipment__form-section">
            <div className="edit-equipment__inputs-container">
              <div className="edit-equipment__two-column">
                <div
                  className="tid-edit-equipment__name
                  edit-equipment__two-column-item"
                >
                  <TwField
                    id="equipmentName"
                    name="equipmentName"
                    value={equipmentName}
                    label="Equipment name or number"
                    inputType={TwInputTypes.text}
                    disabled={false}
                    onInputChange={(e): void => {
                      setEquipmentName(e.target.value);
                    }}
                    error={false}
                  />
                </div>
                <div
                  className="tid-edit-equipment__type
                  edit-equipment__two-column-item"
                >
                  <TwSelect
                    id="equipmentType"
                    name="equipmentType"
                    value={equipmentType}
                    optionList={[
                      { label: 'Truck', value: EquipmentType.truck },
                      { label: 'Trailer', value: EquipmentType.trailer }
                    ]}
                    label="Type"
                    onInputChange={(e): void => {
                      setEquipmentType(e.target.value as EquipmentType);
                    }}
                    disabled={true}
                    error={false}
                  />
                </div>
              </div>
              {enableLiveTracking && equipmentType === EquipmentType.truck && (
                <div className="create-equipment__two-column">
                  {(!!eldConnection?.name || (connections && connections.content?.length > 0)) && (
                    <div
                      className="tid-create-equipment__name
                        create-equipment__two-column-item"
                    >
                      <TwSelect
                        id="eldIntegration"
                        name="eldIntegration"
                        label="ELD integration"
                        value={eldConnection?.id || ''}
                        optionList={connectionsOptions}
                        onInputChange={handleConnectionChange}
                        error={false}
                      />
                    </div>
                  )}

                  {eldConnection && (
                    <div
                      className="tid-create-equipment__type
                        create-equipment__two-column-item"
                    >
                      <TwSelect
                        id="eldAssetId"
                        name="eldAssetId"
                        label="ELD asset ID"
                        value={eldAssetId}
                        optionList={assetIdsOptions}
                        onInputChange={handleAssetChange}
                        disabled={!eldConnection || assetIdsOptions.length === 0}
                        error={false}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </TwDialog>
      <TwWarning
        warningTitle={`Editing ${entityType} on all orders`}
        warningMessage={`By editing this ${entityType} (${equipment?.name}), you will be editing the ${entityType} on all orders with it assigned. Do you want to
      continue?`}
        open={open}
        handleClose={handleClose}
        showWarning={showWarning}
        setShowWarning={setShowWarning}
        reloadId={equipmentId}
      ></TwWarning>
    </>
  );
};

export default EditEquipment;
